import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { uploadFileToAzure } from "@/hooks/usePhotos";
import type {
  FeedMediaUploadUrlRequest,
  FeedMediaUploadUrlResponse,
} from "@/types/generated/cms.generated";

/**
 * Shared SAS image-upload workflow for feed media (stickers, kudos badges).
 *
 * Mirrors the photo-upload pattern used elsewhere in cms-portal:
 *   1. POST `{uploadUrlEndpoint}` to request a short-lived SAS upload URL.
 *   2. PUT the file bytes directly to Azure Blob storage (x-ms-blob-type: BlockBlob).
 *   3. The returned `blobUrl` is then stored on the sticker / kudos type entity.
 *
 * @param uploadUrlEndpoint e.g. "/feed-stickers/upload-url" or "/kudos-types/upload-url"
 */
export function useFeedMediaUpload(uploadUrlEndpoint: string) {
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: async (file: File): Promise<string> => {
      const request: FeedMediaUploadUrlRequest = {
        contentType: file.type || "application/octet-stream",
      };

      const uploadInfo = await apiRequest<FeedMediaUploadUrlResponse>(
        "POST",
        uploadUrlEndpoint,
        request,
      );

      if (!uploadInfo.uploadUrl || !uploadInfo.blobUrl) {
        throw new Error("No upload URL received");
      }

      await uploadFileToAzure(file, uploadInfo.uploadUrl);

      return uploadInfo.blobUrl;
    },
    onError: (error: any) => {
      toast({
        title: "Upload failed",
        description:
          error.message || "Failed to upload image. Please try again.",
        variant: "destructive",
      });
    },
  });

  return {
    /** Uploads a file and resolves with the persisted blob URL. */
    uploadImage: mutation.mutateAsync,
    isUploading: mutation.isPending,
  };
}
